import { createBlockId, starterTemplate } from '../blocks/blockLibrary';
import type { EmailBlock, SocialLink } from '../HtmlEditor.types';
import { sanitizeBlockHtml } from './richText';

/**
 * Best-effort parser for HTML emitted by serializeBlocksToHtml.
 * Does not handle arbitrary third-party HTML — only our table-based output shape.
 */
export function parseHtmlToBlocks(html: string): EmailBlock[] {
  const trimmed = html?.trim();
  if (!trimmed) {
    return starterTemplate();
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(trimmed, 'text/html');
    const innerTable = doc.querySelector('table[role="presentation"] table[role="presentation"]');
    if (!innerTable) {
      return starterTemplate();
    }

    const rows = Array.from(innerTable.querySelectorAll('tr'));
    const blocks: EmailBlock[] = [];

    for (const row of rows) {
      const td = row.querySelector('td');
      if (!td) continue;

      const style = td.getAttribute('style') ?? '';
      const padding = parseBox(style, 'padding');
      const margin = parseBox(style, 'margin');
      const align = (style.match(/text-align:\s*(\w+)/)?.[1] ?? 'left') as EmailBlock['align'];
      const bgMatch = style.match(/background-color:\s*([^;]+)/);
      const bg = bgMatch?.[1]?.trim() ?? 'transparent';

      const base = {
        id: createBlockId(),
        align,
        width: 100,
        paddingTop: padding[0],
        paddingRight: padding[1],
        paddingBottom: padding[2],
        paddingLeft: padding[3],
        marginTop: margin[0],
        marginRight: margin[1],
        marginBottom: margin[2],
        marginLeft: margin[3],
        bg,
        bgImage: '',
      };

      const h2 = td.querySelector('h2') as HTMLHeadingElement | null;
      if (h2) {
        blocks.push({
          ...base,
          type: 'title',
          text: sanitizeBlockHtml(h2.innerHTML),
          size: parseInt(h2.style.fontSize || '30', 10) || 30,
          color: h2.style.color || '#0F172A',
          font: h2.style.fontFamily?.split(',')[0]?.replace(/['"]/g, '') || 'Arial',
          weight: parseInt(h2.style.fontWeight || '700', 10) || 700,
          lineHeight: parseFloat(h2.style.lineHeight || '1.2') || 1.2,
        });
        continue;
      }

      const p = td.querySelector('p') as HTMLParagraphElement | null;
      if (p) {
        blocks.push({
          ...base,
          type: 'text',
          text: sanitizeBlockHtml(p.innerHTML),
          size: parseInt(p.style.fontSize || '14', 10) || 14,
          color: p.style.color || '#334155',
          font: p.style.fontFamily?.split(',')[0]?.replace(/['"]/g, '') || 'Arial',
          weight: parseInt(p.style.fontWeight || '400', 10) || 400,
          lineHeight: parseFloat(p.style.lineHeight || '1.6') || 1.6,
        });
        continue;
      }

      const img = td.querySelector('img') as HTMLImageElement | null;
      if (img) {
        blocks.push({
          ...base,
          type: 'image',
          src: img.getAttribute('src') ?? '',
          placeholder: '🌅',
          height: parseInt(img.style.height || '220', 10) || 220,
          align: 'center',
        });
        continue;
      }

      const socialBlock = parseSocialBlock(td, base);
      if (socialBlock) {
        blocks.push(socialBlock);
        continue;
      }

      const anchor = td.querySelector('a[style*="display:inline-block"]') as HTMLAnchorElement | null;
      if (anchor) {
        blocks.push({
          ...base,
          type: 'button',
          text: anchor.textContent ?? '',
          href: anchor.getAttribute('href') ?? '#',
          btnBg: anchor.style.backgroundColor || '#0F172A',
          btnColor: anchor.style.color || '#FFFFFF',
          radius: parseInt(anchor.style.borderRadius || '6', 10) || 6,
          size: parseInt(anchor.style.fontSize || '14', 10) || 14,
          align: 'center',
          paddingTop: 14,
          paddingBottom: 14,
        });
        continue;
      }

      const hr = td.querySelector('hr') as HTMLHRElement | null;
      if (hr) {
        blocks.push({
          ...base,
          type: 'divider',
          color: hr.style.backgroundColor || '#E2E8F0',
          thickness: parseInt(hr.style.height || '1', 10) || 1,
          paddingTop: 8,
          paddingBottom: 8,
        });
        continue;
      }

      const spacerDiv = td.querySelector('div[style*="height"]') as HTMLDivElement | null;
      if (spacerDiv && !spacerDiv.querySelector('*') && spacerDiv.textContent?.trim() === '') {
        const h = parseInt(spacerDiv.style.height || '24', 10) || 24;
        if (h <= 48) {
          blocks.push({ ...base, type: 'spacer', height: h });
          continue;
        }
      }
    }

    return blocks.length > 0 ? blocks : starterTemplate();
  } catch {
    return starterTemplate();
  }
}

function parseSocialBlock(td: Element, base: Omit<EmailBlock, 'type'> & { type?: never }): EmailBlock | null {
  const dataNetworkAnchors = Array.from(td.querySelectorAll('a[data-network]')) as HTMLAnchorElement[];
  if (dataNetworkAnchors.length > 0) {
    const links: SocialLink[] = dataNetworkAnchors.map((anchor) => {
      const href = anchor.getAttribute('href') ?? '#';
      return {
        network: anchor.getAttribute('data-network') ?? '',
        url: href === '#' ? '' : href,
      };
    });
    return { ...base, type: 'social', links, align: 'center' };
  }

  const circularAnchors = Array.from(td.querySelectorAll('a[style*="border-radius:50%"]')) as HTMLAnchorElement[];
  if (circularAnchors.length > 0) {
    const defaultNetworks = ['facebook', 'instagram', 'linkedin', 'x', 'youtube', 'tiktok'];
    const links: SocialLink[] = circularAnchors.map((anchor, index) => {
      const href = anchor.getAttribute('href') ?? '#';
      return {
        network: defaultNetworks[index] ?? `network-${index}`,
        url: href === '#' ? '' : href,
      };
    });
    return { ...base, type: 'social', links, align: 'center' };
  }

  return null;
}

function parseBox(style: string, prop: 'padding' | 'margin'): [number, number, number, number] {
  const match = style.match(new RegExp(`${prop}:\\s*([^;]+)`));
  if (!match) return [0, 0, 0, 0];
  const parts = match[1].split(/\s+/).map((v) => parseInt(v, 10) || 0);
  if (parts.length === 1) return [parts[0], parts[0], parts[0], parts[0]];
  if (parts.length === 2) return [parts[0], parts[1], parts[0], parts[1]];
  if (parts.length === 3) return [parts[0], parts[1], parts[2], parts[1]];
  return [parts[0], parts[1], parts[2], parts[3]];
}
